import { baseApi } from './baseApi'
import type {
  DataResponse,
  GoodAllocationCheckTruckId,
  GoodAllocationContractSnapshot,
  GoodAllocationDeleteMany,
  GoodAllocationListItem,
  GoodAllocationListQuery,
  GoodAllocationLookupResponse,
  GoodAllocationReceiptSnapshot,
  GoodAllocationSuggestTruckId,
  GoodAllocationTruckStatusChange,
  GoodAllocationWrite,
  InvoiceContractSnapshot,
  InvoiceDeclarationDateWrite,
  InvoiceDetail,
  InvoiceListItem,
  InvoiceListQuery,
  InvoiceLookupResponse,
  InvoiceSuggestHoadonItem,
  InvoiceSuggestHoadonSaveResult,
  InvoiceSuggestHoadonSaveRow,
  InvoiceWrite,
  MessageResponse,
  PageResponse,
  PayDataDetail,
  PayDataListItem,
  PayDataListQuery,
  PayDataLookupResponse,
  PayDataMailDraft,
  PayDataPrintResult,
  PayDataWrite,
} from '@/types/api'
// ===================== procurementContractDetailApi =====================
/**
 * contractDetailApi — RTK Query slice cho màn "Contract Detail" (Procurement Control).
 *
 * Access `frmProcurementMainSubFormDetailsNPE/…DetailsP`: header 1 hợp đồng (2 picker)
 * + Tab1 "Pay Info" (3 datasheet GoodsAlc/Paydata/Invoice), NPE thêm Advance/Fixation.
 * Web truyền `contractNo` (stateless — không temp table).
 *
 *   GET /purchase/contract-detail/contracts
 *   GET /purchase/contract-detail/header?variant=p|npe&contractNo=...
 *   GET /purchase/contract-detail/goods-alc?contractNo=...
 *   GET /purchase/contract-detail/paydata?contractNo=...
 *   GET /purchase/contract-detail/invoice?contractNo=...
 *   GET /purchase/contract-detail/advance?contractNo=...
 *   GET /purchase/contract-detail/fixation?contractNo=...
 *
 * Đọc snake_case raw (như ProcurementDatasheet) → field giữ nguyên snake_case.
 */

export interface ContractPickP {
  contract_no: string
  npe_ref: string | null
  contract_date: string | null
  contract_type: string | null
  latest_delivery: string | null
  supplier_name: string | null
}

export interface ContractPickNPE {
  contract_no: string
  contract_date: string | null
  supplier_name: string | null
  latest_delivery: string | null
}

export interface ContractPicks {
  p: ContractPickP[]
  npe: ContractPickNPE[]
}

/** Header P — 1 row mv_local_p (Access ProcurementPayDataQryLocalP_pick). */
export interface HeaderP {
  contract_no: string | null
  npe_ref: string | null
  delivery_place: string | null
  supplier_name: string | null
  price_unit: string | null
  avg_futures_price_real: number | null
  quality_code: string | null
  certification: string | null
  contract_type: string | null
  outright_price: number | null
  ptbf_diffs: number | null
  avg_price2_real: number | null
  latest_delivery: string | null
  futures_month: string | null
  intermediary_text: string | null
  quantity_kg: number | null
  total_lot: number | null
  contract_shipper_intermediary: string | null
  contract_date: string | null
  lot_fixed: number | null
  shipper_name_short: string | null
  received: number | null
  kg_fixed: number | null
  fixed_fx: number | null
  to_be_received: number | null
  kg_to_fix: number | null
  price_diffs_2: number | null
  is_forced_close_int: number | null
  fix_level: number | null
  [key: string]: unknown
}

/** Header NPE — 1 row v_consignment_p (Access ProcurementPayDataQryNPE_pick). */
export interface HeaderNPE {
  contract_no: string | null
  certification: string | null
  contract_date: string | null
  delivery_time: string | null
  package_name: string | null
  cp_code: string | null
  quality_code: string | null
  interest_rate_note: string | null
  cp_name: string | null
  supplier_name: string | null
  quantity_kg: number | null
  paid_vnd: number | null
  delivery_place: string | null
  no_bag: number | null
  note: string | null
  stoploss_rate: number | null
  provisional_market_rate: number | null
  received: number | null
  advanced: number | null
  fixed: number | null
  to_be_receive: number | null
  to_be_advance: number | null
  to_be_fix: number | null
  [key: string]: unknown
}

export interface GoodsAlcRow {
  id: number | null
  contract_no: string | null
  m_date: string | null
  m_truck_no: string | null
  truck_id: string | null
  truck_status: string | null
  estimated_quantity: number | null
  allocating_quantity: number | null
  requested_qry: number | null
  requested_provi: number | null
  to_be_requested_provi: number | null
  requested_fix: number | null
  to_be_requested_fix: number | null
  to_warehouse: string | null
  receipt_no: string | null
  [key: string]: unknown
}

export interface InvoiceRow {
  invoice_id: number | null
  warehouse: string | null
  invoice_no: string | null
  invoice_date: string | null
  quantity: number | null
  price_vnd_kg: number | null
  total_payment: number | null
  total_amount: number | null
  vat_amount: number | null
  total_amount_after_tax: number | null
  [key: string]: unknown
}

export interface PaydataRow {
  paydata_p_id: number | null
  contract_no: string | null
  contract_show: string | null
  payment_type: string | null
  xntt_no: string | null
  truck_id: string | null
  bank: string | null
  quantity: number | null
  payment_date: string | null
  requested_usd: number | null
  requested_fx: number | null
  requested_vnd: number | null
  paid_usd: number | null
  fx: number | null
  paid_vnd: number | null
  reconcile: number | null
  invoice_no: string | null
  stoploss: number | null
  advanced: number | null
  vat_amount_usd: number | null
  vat_amount_vnd: number | null
  note: string | null
  [key: string]: unknown
}

export interface AdvanceRow {
  paydata_p_id: number | null
  payment_date: string | null
  contract_no: string | null
  xntt_no: string | null
  payment_type: string | null
  bank: string | null
  quantity: number | null
  price: number | null
  requested_usd: number | null
  requested_fx: number | null
  requested_vnd: number | null
  vat_amount_vnd: number | null
  paid_usd: number | null
  fx: number | null
  paid_vnd: number | null
  reconcile: number | null
  interest_rate: number | null
  fixation_deadline: string | null
  invoice_no: string | null
  invoice_date: string | null
  invoice_amount: number | null
  warehouse: string | null
  note: string | null
  supplier_id: string | null
  bank_account: string | null
  account_name: string | null
  bank_name: string | null
  contract_date: string | null
  quality_code: string | null
  date_update: string | null
  person_update: string | null
  must_fix: number | null
  must_fix_txt: string | null
  [key: string]: unknown
}

export interface FixationRow {
  cs_fixation_id: number | null
  fixation_date: string | null
  fixation_no: string | null
  contract_no: string | null
  cp_code: string | null
  name: string | null
  quality_code: string | null
  quantity_kg: number | null
  price: number | null
  p_contract_no: string | null
  price_after_interest: number | null
  to_be_paid: number | null
  total_amount: number | null
  allocated_amount: number | null
  allocated_interest: number | null
  allocated_quantity: number | null
  p_paid: number | null
  note: string | null
  person_update: string | null
  date_update: string | null
  [key: string]: unknown
}

// Tags để invalidate list sau khi Delete/Save (Access Requery sau ghi).
const taggedBase = baseApi.enhanceEndpoints({
  addTagTypes: [
    'CDPaydata',
    'CDInvoice',
    'CDGoodsAlc',
    'CDAdvance',
    'CDFixation',
  ],
})

export const contractDetailApi = taggedBase.injectEndpoints({
  endpoints: (b) => ({
    getContractPicks: b.query<ContractPicks, void>({
      query: () => ({ url: '/purchase/contract-detail/contracts' }),
      // ChooseContract dropdown — refetch qua Reload (ContractAll trong baseTags của ProcurementPage).
      providesTags: [{ type: 'ContractAll', id: 'LIST' }],
    }),
    getContractHeaderP: b.query<HeaderP, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/header',
        params: { variant: 'p', contractNo },
      }),
      providesTags: (_r, _e, arg) => [{ type: 'ContractAll', id: arg.contractNo }],
    }),
    getContractHeaderNPE: b.query<HeaderNPE, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/header',
        params: { variant: 'npe', contractNo },
      }),
      providesTags: (_r, _e, arg) => [{ type: 'ContractAll', id: arg.contractNo }],
    }),
    listContractGoodsAlc: b.query<PageResponse<GoodsAlcRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/goods-alc',
        params: { contractNo },
      }),
      providesTags: ['CDGoodsAlc'],
    }),
    listContractInvoice: b.query<PageResponse<InvoiceRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/invoice',
        params: { contractNo },
      }),
      providesTags: ['CDInvoice'],
    }),
    listContractPaydata: b.query<PageResponse<PaydataRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/paydata',
        params: { contractNo },
      }),
      providesTags: ['CDPaydata'],
    }),
    listContractAdvance: b.query<PageResponse<AdvanceRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/advance',
        params: { contractNo },
      }),
      providesTags: ['CDAdvance'],
    }),
    listContractFixation: b.query<PageResponse<FixationRow>, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/contract-detail/fixation',
        params: { contractNo },
      }),
      providesTags: ['CDFixation'],
    }),
    // Access Tab2 btnDelete — guard npe_allocation(advanced_no) BE-side.
    deleteContractAdvance: b.mutation<{ ok: boolean }, { paydataId: number }>({
      query: ({ paydataId }) => ({
        url: `/purchase/contract-detail/advance/${paydataId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CDAdvance', 'CDPaydata'],
    }),
    // Access Tab3 btnDelete — soft-delete 4 bảng BE-side (guards allocated/paid).
    deleteContractFixation: b.mutation<{ ok: boolean }, { fixationId: number }>({
      query: ({ fixationId }) => ({
        url: `/purchase/contract-detail/fixation/${fixationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CDFixation', 'CDPaydata', 'CDAdvance'],
    }),
  }),
})

export const {
  useGetContractPicksQuery,
  useGetContractHeaderPQuery,
  useGetContractHeaderNPEQuery,
  useListContractGoodsAlcQuery,
  useListContractInvoiceQuery,
  useListContractPaydataQuery,
  useListContractAdvanceQuery,
  useListContractFixationQuery,
  useDeleteContractAdvanceMutation,
  useDeleteContractFixationMutation,
} = contractDetailApi

/** Invalidate list con sau khi dialog ghi xong (Write data / New / Edit / Add Pay). */
export const invalidateContractDetailLists = () =>
  contractDetailApi.util.invalidateTags([
    'CDPaydata',
    'CDInvoice',
    'CDGoodsAlc',
    'CDAdvance',
    'CDFixation',
  ])

// ===================== procurementContractDetailInvoiceApi =====================
/**
 * contractDetailInvoiceApi — RTK Query slice cho dialog Contract Detail →
 * Add Invoice (batch). File RIÊNG (không sửa contractDetailApi.ts dùng chung).
 *
 * Backend: app/api/v1/endpoints/purchase/contract_detail_invoice.py
 *   POST /purchase/contract-detail/invoice-batch
 *
 * Types snake_case khớp BE (app/models/purchase/contract_detail_invoice.py).
 */

/** Một dòng grid nhập (Access tmp_addmany_pay_invoice). */
export interface InvoiceBatchRowIn {
  warehouse: string | null
  invoice_no: string | null
  /** 'YYYY-MM-DD' */
  invoice_date: string | null
  quantity: number | null
  price_vnd_kg: number | null
  total_amount: number | null
  vat_amount: number | null
  description: string | null
}

export interface InvoiceBatchRequest {
  contract_no: string
  rows: InvoiceBatchRowIn[]
}

/** Access MsgBox "Raw records: N / Error: M". */
export interface InvoiceBatchResult {
  raw_records: number
  errors: number
}

export const contractDetailInvoiceApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    saveInvoiceBatch: b.mutation<InvoiceBatchResult, InvoiceBatchRequest>({
      query: (body) => ({
        url: '/purchase/contract-detail/invoice-batch',
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
})

export const { useSaveInvoiceBatchMutation } = contractDetailInvoiceApi

// ===================== procurementContractDetailOutrightApi =====================
/**
 * contractDetailOutrightApi — RTK Query slice cho Contract Detail
 * "Add Pay Outright" batch dialog (artifact C2).
 *
 * BE: /purchase/contract-detail/outright/preview + /outright/save
 * (backend/app/api/v1/endpoints/purchase/contract_detail_outright.py).
 * Types snake_case khớp model BE (không camelCase alias).
 */

export interface OutrightRowInput {
  payment_type?: string | null
  truck_id?: string | null
  bank_name?: string | null
  quantity_kg?: number | null
  advanced_vnd?: number | null
  advanced_sub_vnd?: number | null
  /** Percent 0–100 (BE quy về fraction Access). */
  pct_pay?: number | null
  description?: string | null
  vat_amount_vnd?: number | null
}

export interface OutrightPreviewRequest {
  contract_no: string
  /** Parse từ ô "Requested FX" màn cha; null → BE fallback fixed_fx. */
  requested_fx?: number | null
  rows: OutrightRowInput[]
}

export interface OutrightSaveRequest extends OutrightPreviewRequest {
  /** 1 giá trị XNTT No chung cho mọi dòng. */
  xntt_no?: string | null
}

export interface OutrightPreviewRow {
  date: string
  contract_no: string
  payment_type: string | null
  truck_id: string | null
  price: number | null
  price_unit: string | null
  requested_fx: number | null
  bank_name: string | null
  quantity_kg: number | null
  to_be_requested: number | null
  advanced_vnd: number | null
  request_vnd: number | null
  vat_amount_vnd: number | null
  advanced_sub_vnd: number | null
  request_sub_vnd: number | null
  description: string | null
}

export interface OutrightPreviewResponse {
  rows: OutrightPreviewRow[]
  /** "To be Receive" — mv_local_p.pending. */
  pending: number | null
  fixed_fx: number | null
  vat_txt: string | null
  /** "Pending.Outright" — SUM(to_be_requested). */
  pending_outright: number | null
  /** Requested FX resolved (payload ?? fixed_fx). */
  requested_fx: number | null
  /** "New Pending.Outright" = pending_outright − SUM(Quantity (KG)). */
  new_pending: number | null
  contract_type: string | null
  price_unit: string | null
}

export interface OutrightSaveResponse {
  raw_records: number
  errors: number
}

export const contractDetailOutrightApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    outrightPreview: b.mutation<OutrightPreviewResponse, OutrightPreviewRequest>({
      query: (body) => ({
        url: '/purchase/contract-detail/outright/preview',
        method: 'POST',
        body,
      }),
    }),
    outrightSave: b.mutation<OutrightSaveResponse, OutrightSaveRequest>({
      query: (body) => ({
        url: '/purchase/contract-detail/outright/save',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'PayData', id: 'LIST' }],
    }),
  }),
})

export const { useOutrightPreviewMutation, useOutrightSaveMutation } = contractDetailOutrightApi

// ===================== procurementContractDetailPtbfApi =====================
/**
 * contractDetailPtbfApi — RTK Query slice cho Contract Detail "Add Pay PTBF"
 * batch dialog (artifact C3, Access frmProcurementQryDetailsAddPayPTBF).
 *
 * Endpoints (BE app/api/v1/endpoints/purchase/contract_detail_ptbf.py):
 *   POST /purchase/contract-detail/ptbf/preview — compute chain + header context
 *   POST /purchase/contract-detail/ptbf/save    — batch insert paydata
 *
 * Types snake_case khớp BE models (app/models/purchase/contract_detail_ptbf.py).
 */

/** 1 dòng user nhập trong input grid (đa số optional — BE tự derive). */
export interface PtbfRowInput {
  payment_type?: string | null
  truck_id?: string | null
  bank_name?: string | null
  quantity_kg?: number | null
  pct_pay?: number | null
  advanced_usd?: number | null
  price?: number | null
  futures?: number | null
  requested_fx?: number | null
  price_unit?: string | null
  sub_price?: number | null
  advanced_sub_usd?: number | null
  request_sub_usd?: number | null
  request_sub_vnd?: number | null
  description?: string | null
  vat_amount_vnd?: number | null
}

export interface PtbfPreviewRequest {
  contract_no: string
  requested_fx?: number | null
  futures?: number | null
  rows: PtbfRowInput[]
}

export interface PtbfSaveRequest extends PtbfPreviewRequest {
  xntt_no?: string | null
}

/** 1 dòng preview read-only (output query ProcurementPay_WritePTBF). */
export interface PtbfPreviewRow {
  date: string
  contract_no: string
  payment_type: string | null
  truck_id: string | null
  bank_name: string | null
  to_be_requested: number | null
  quantity_kg: number | null
  price: number | null
  futures: number | null
  price_unit: string | null
  requested_fx: number | null
  advanced_usd: number | null
  request_usd: number | null
  request_vnd: number | null
  vat_amount_vnd: number | null
  description: string | null
  sub_price: number | null
  request_sub_usd: number | null
  request_sub_vnd: number | null
  advanced_sub_usd: number | null
}

/** Field context header dialog (read-only). */
export interface PtbfContext {
  fixed_fx: number | null
  provi_market_rate: number | null
  vat_txt: string | null
  pending: number | null
  pending_provisional: number | null
  pending_fixation: number | null
  requested_fx: number | null
}

export interface PtbfPreviewResponse {
  rows: PtbfPreviewRow[]
  new_pending_provisional: number | null
  new_pending_fixation: number | null
  context: PtbfContext
}

export interface PtbfSaveResponse {
  raw_records: number
  errors: number
}

export const contractDetailPtbfApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    ptbfPreview: b.mutation<PtbfPreviewResponse, PtbfPreviewRequest>({
      query: (body) => ({
        url: '/purchase/contract-detail/ptbf/preview',
        method: 'POST',
        body,
      }),
    }),
    ptbfSave: b.mutation<PtbfSaveResponse, PtbfSaveRequest>({
      query: (body) => ({
        url: '/purchase/contract-detail/ptbf/save',
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const { usePtbfPreviewMutation, usePtbfSaveMutation } = contractDetailPtbfApi

// ===================== procurementCvbbApi =====================
/**
 * procurementCvbbApi — RTK Query slice cho /purchase/cvbb/* (Công văn & Biên bản,
 * Access `frmProcurementMainSubFormCVBB`). List đọc VIEW public.v_congvan_bienban;
 * CRUD ghi base table public.congvan_bienban (soft delete is_status=1).
 * Tag types: 'ProcCvbb' (list) + 'ProcCvbbDetail' (single/form snapshot).
 */

export interface CvbbListItem {
  cvbb_id: number
  cvbb_date: string | null
  cvbb_type: string | null
  cvbb_no: string | null
  contract_no: string | null
  client_short: string | null
  info_txt: string | null
  invoice_no: string | null
  note: string | null
  date_updated: string | null
  person_updated: string | null
  cvbb_vn: string | null
  cvbb_eng: string | null
  cvbb_full_no: string | null
}

export interface CvbbListResponse {
  items: CvbbListItem[]
  total: number
}

/** Snapshot cho dialog Edit/Copy — field của form Add + contract tách P/S. */
export interface CvbbFormSnapshot {
  cvbb_id: number
  cvbb_no: string | null
  cvbb_date: string | null
  cvbb_type: string | null
  p_contract: string | null
  s_contract: string | null
  info_txt: string | null
  invoice_no: string | null
  note: string | null
}

export interface CvbbWrite {
  cvbb_no: string | null
  cvbb_date: string | null
  cvbb_type: string | null
  /** contract_no đã resolve (P→p_contract, S→s_contract) ở FE trước khi gửi. */
  contract_no: string | null
  info_txt: string | null
  invoice_no: string | null
  note: string | null
}

export interface CvbbLookupItem {
  value: string
  label: string
}
export interface CvbbLookupResponse {
  items: CvbbLookupItem[]
  total: number
}
export interface CvbbDataResponse<T> {
  data: T
  message?: string
}
export interface CvbbMessageResponse {
  message: string
}

export const procurementCvbbApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listCvbb: b.query<CvbbListResponse, { page?: number; pageSize?: number } | void>({
      query: (params) => ({ url: '/purchase/cvbb/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'ProcCvbb' as const, id: it.cvbb_id })),
              { type: 'ProcCvbb' as const, id: 'LIST' },
            ]
          : [{ type: 'ProcCvbb' as const, id: 'LIST' }],
    }),
    getCvbbForm: b.query<CvbbDataResponse<CvbbFormSnapshot>, number>({
      query: (id) => `/purchase/cvbb/${id}/form`,
      providesTags: (_r, _e, id) => [{ type: 'ProcCvbbDetail', id }],
      keepUnusedDataFor: 0,
    }),
    createCvbb: b.mutation<CvbbDataResponse<CvbbFormSnapshot>, CvbbWrite>({
      query: (body) => ({ url: '/purchase/cvbb/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcCvbb', id: 'LIST' }],
    }),
    updateCvbb: b.mutation<CvbbDataResponse<CvbbFormSnapshot>, { id: number; body: CvbbWrite }>({
      query: ({ id, body }) => ({ url: `/purchase/cvbb/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ProcCvbb', id: arg.id },
        { type: 'ProcCvbb', id: 'LIST' },
        { type: 'ProcCvbbDetail', id: arg.id },
      ],
    }),
    deleteCvbb: b.mutation<CvbbMessageResponse, number>({
      query: (id) => ({ url: `/purchase/cvbb/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ProcCvbb', id },
        { type: 'ProcCvbb', id: 'LIST' },
      ],
    }),
    // Lookups
    cvbbPContractsLookup: b.query<CvbbLookupResponse, { q?: string } | void>({
      query: (arg) => ({ url: '/purchase/cvbb/lookups/p-contracts', params: arg && arg.q ? { q: arg.q } : {} }),
    }),
    cvbbSContractsLookup: b.query<CvbbLookupResponse, { q?: string } | void>({
      query: (arg) => ({ url: '/purchase/cvbb/lookups/s-contracts', params: arg && arg.q ? { q: arg.q } : {} }),
    }),
    cvbbTypesLookup: b.query<CvbbLookupResponse, void>({
      query: () => '/purchase/cvbb/lookups/cvbb-types',
    }),
    cvbbInvoicesLookup: b.query<CvbbLookupResponse, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/cvbb/lookups/invoices',
        params: { contractNo },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useListCvbbQuery,
  useGetCvbbFormQuery,
  useLazyGetCvbbFormQuery,
  useCreateCvbbMutation,
  useUpdateCvbbMutation,
  useDeleteCvbbMutation,
  useCvbbPContractsLookupQuery,
  useLazyCvbbPContractsLookupQuery,
  useCvbbSContractsLookupQuery,
  useLazyCvbbSContractsLookupQuery,
  useCvbbTypesLookupQuery,
  useLazyCvbbInvoicesLookupQuery,
} = procurementCvbbApi

// ===================== procurementGoodAllocationApi =====================
/**
 * procurementGoodAllocationApi — RTK Query slice cho /purchase/good-allocations/*
 * (Procurement Goods Allocation, Access frmProcurementMainSubFormGoodAlc).
 * Tag types: 'ProcGoodAlc' (list) + 'ProcGoodAlcDetail' (single).
 */

export const procurementGoodAllocationApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listGoodAllocations: b.query<PageResponse<GoodAllocationListItem>, GoodAllocationListQuery | void>({
      query: (params) => ({ url: '/purchase/good-allocations/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'ProcGoodAlc' as const, id: it.id })),
              { type: 'ProcGoodAlc' as const, id: 'LIST' },
            ]
          : [{ type: 'ProcGoodAlc' as const, id: 'LIST' }],
    }),
    getGoodAllocationForm: b.query<GoodAllocationListItem, number>({
      query: (id) => `/purchase/good-allocations/${id}/form`,
      providesTags: (_r, _e, id) => [{ type: 'ProcGoodAlcDetail', id }],
    }),
    createGoodAllocation: b.mutation<DataResponse<number>, GoodAllocationWrite>({
      query: (body) => ({ url: '/purchase/good-allocations', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcGoodAlc', id: 'LIST' }],
    }),
    updateGoodAllocation: b.mutation<MessageResponse, { id: number; body: GoodAllocationWrite }>({
      query: ({ id, body }) => ({ url: `/purchase/good-allocations/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ProcGoodAlc', id: arg.id },
        { type: 'ProcGoodAlc', id: 'LIST' },
        { type: 'ProcGoodAlcDetail', id: arg.id },
      ],
    }),
    deleteGoodAllocation: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/purchase/good-allocations/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ProcGoodAlc', id },
        { type: 'ProcGoodAlc', id: 'LIST' },
      ],
    }),
    deleteManyGoodAllocations: b.mutation<MessageResponse, GoodAllocationDeleteMany>({
      query: (body) => ({ url: '/purchase/good-allocations/delete-many', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcGoodAlc', id: 'LIST' }],
    }),
    changeGoodAllocationTruckStatus: b.mutation<MessageResponse, GoodAllocationTruckStatusChange>({
      query: (body) => ({ url: '/purchase/good-allocations/change-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcGoodAlc', id: 'LIST' }],
    }),
    addManyGoodAllocations: b.mutation<DataResponse<number[]>, GoodAllocationWrite[]>({
      query: (rows) => ({ url: '/purchase/good-allocations/add-many', method: 'POST', body: rows }),
      invalidatesTags: [{ type: 'ProcGoodAlc', id: 'LIST' }],
    }),
    suggestGoodAllocationTruckId: b.query<GoodAllocationSuggestTruckId, void>({
      query: () => '/purchase/good-allocations/suggest/truck-id',
      keepUnusedDataFor: 0,
    }),
    checkGoodAllocationTruckId: b.query<GoodAllocationCheckTruckId, { truckId: string; excludeId?: number }>({
      query: ({ truckId, excludeId }) => ({
        url: '/purchase/good-allocations/check/truck-id',
        params: { truck_id: truckId, ...(excludeId ? { exclude_id: excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    printGoodAllocationBbgn: b.query<{ title: string; html: string; message: string }, number>({
      query: (id) => `/purchase/good-allocations/${id}/print/bbgn`,
      keepUnusedDataFor: 0,
    }),
    // Lookups
    goodAllocPContractLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/p-contract',
    }),
    goodAllocConsignmentLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/consignment',
    }),
    goodAllocGradeLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/grade',
    }),
    goodAllocWarehouseLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/warehouse',
    }),
    goodAllocInspectionLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/inspection',
    }),
    goodAllocTruckStatusLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/truck-status',
    }),
    goodAllocReceiptGrnLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/receipt-grn',
    }),
    goodAllocReceiptRdnLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/receipt-rdn',
    }),
    // Snapshot cascades (Access *_AfterUpdate) — fetch khi pick combo.
    goodAllocContractSnapshot: b.query<
      GoodAllocationContractSnapshot,
      { contractNo: string; kind: 'p' | 'consignment' }
    >({
      query: ({ contractNo, kind }) => ({
        url: '/purchase/good-allocations/lookups/contract-snapshot',
        params: { contract_no: contractNo, kind },
      }),
      keepUnusedDataFor: 0,
    }),
    goodAllocReceiptSnapshot: b.query<
      GoodAllocationReceiptSnapshot,
      { receiptNo: string; kind: 'GRN' | 'RDN' }
    >({
      query: ({ receiptNo, kind }) => ({
        url: '/purchase/good-allocations/lookups/receipt-snapshot',
        params: { receipt_no: receiptNo, kind },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useListGoodAllocationsQuery,
  useGetGoodAllocationFormQuery,
  useLazyGetGoodAllocationFormQuery,
  useCreateGoodAllocationMutation,
  useUpdateGoodAllocationMutation,
  useDeleteGoodAllocationMutation,
  useDeleteManyGoodAllocationsMutation,
  useChangeGoodAllocationTruckStatusMutation,
  useAddManyGoodAllocationsMutation,
  useSuggestGoodAllocationTruckIdQuery,
  useLazySuggestGoodAllocationTruckIdQuery,
  useLazyPrintGoodAllocationBbgnQuery,
  useLazyCheckGoodAllocationTruckIdQuery,
  useGoodAllocPContractLookupQuery,
  useGoodAllocConsignmentLookupQuery,
  useGoodAllocGradeLookupQuery,
  useGoodAllocWarehouseLookupQuery,
  useGoodAllocInspectionLookupQuery,
  useGoodAllocTruckStatusLookupQuery,
  useGoodAllocReceiptGrnLookupQuery,
  useGoodAllocReceiptRdnLookupQuery,
  useLazyGoodAllocContractSnapshotQuery,
  useLazyGoodAllocReceiptSnapshotQuery,
} = procurementGoodAllocationApi

// ===================== procurementInvoiceApi =====================
/**
 * procurementInvoiceApi — RTK Query slice cho /purchase/invoices/* (Procurement Invoice).
 * Tag types: 'ProcInvoice' (list) + 'ProcInvoiceDetail' (single).
 */

export const procurementInvoiceApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listInvoices: b.query<PageResponse<InvoiceListItem>, InvoiceListQuery | void>({
      query: (params) => ({ url: '/purchase/invoices/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'ProcInvoice' as const, id: it.invoice_id })),
              { type: 'ProcInvoice' as const, id: 'LIST' },
            ]
          : [{ type: 'ProcInvoice' as const, id: 'LIST' }],
    }),
    getInvoice: b.query<DataResponse<InvoiceDetail>, number>({
      query: (id) => `/purchase/invoices/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'ProcInvoiceDetail', id }],
    }),
    createInvoice: b.mutation<DataResponse<InvoiceDetail>, InvoiceWrite>({
      query: (body) => ({ url: '/purchase/invoices/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcInvoice', id: 'LIST' }],
    }),
    updateInvoice: b.mutation<DataResponse<InvoiceDetail>, { id: number; body: InvoiceWrite }>({
      query: ({ id, body }) => ({ url: `/purchase/invoices/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ProcInvoice', id: arg.id },
        { type: 'ProcInvoice', id: 'LIST' },
        { type: 'ProcInvoiceDetail', id: arg.id },
      ],
    }),
    deleteInvoice: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/purchase/invoices/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ProcInvoice', id },
        { type: 'ProcInvoice', id: 'LIST' },
      ],
    }),
    /** Multi-delete (Access btnDelete trên ticked rows) — xoá 1 lần cả mảng ids. */
    bulkDeleteInvoice: b.mutation<MessageResponse, number[]>({
      query: (ids) => ({ url: '/purchase/invoices/delete-many', method: 'POST', body: ids }),
      invalidatesTags: [{ type: 'ProcInvoice', id: 'LIST' }],
    }),
    /** Bulk set declaration_date + receive_geo_date cho các invoice ticked. */
    setInvoiceDeclarationDate: b.mutation<MessageResponse, InvoiceDeclarationDateWrite>({
      query: (body) => ({ url: '/purchase/invoices/declaration-date', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcInvoice', id: 'LIST' }],
    }),
    // Accounting - Input Invoices (suggest hoadon)
    listInvoiceSuggestHoadon: b.query<PageResponse<InvoiceSuggestHoadonItem>, void>({
      query: () => '/purchase/invoices/suggest-hoadon/list',
      keepUnusedDataFor: 0,
    }),
    saveInvoiceSuggestHoadon: b.mutation<
      DataResponse<InvoiceSuggestHoadonSaveResult>,
      { rows: InvoiceSuggestHoadonSaveRow[] }
    >({
      query: (body) => ({ url: '/purchase/invoices/suggest-hoadon/save', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcInvoice', id: 'LIST' }],
    }),
    // Contract snapshot (DLookup bundle cho dialog Add)
    invoiceContractSnapshot: b.query<
      DataResponse<InvoiceContractSnapshot>,
      { contractNo: string }
    >({
      query: ({ contractNo }) =>
        `/purchase/invoices/contract-snapshot?contractNo=${encodeURIComponent(contractNo)}`,
      keepUnusedDataFor: 0,
    }),
    // Lookups
    invoicePContractsLookup: b.query<InvoiceLookupResponse, { q?: string } | void>({
      query: (arg) => ({
        url: '/purchase/invoices/lookups/p-contracts',
        params: arg && arg.q ? { q: arg.q } : {},
      }),
    }),
    invoiceConsignmentContractsLookup: b.query<InvoiceLookupResponse, { q?: string } | void>({
      query: (arg) => ({
        url: '/purchase/invoices/lookups/consignment-contracts',
        params: arg && arg.q ? { q: arg.q } : {},
      }),
    }),
    invoiceAllContractsLookup: b.query<InvoiceLookupResponse, { q?: string } | void>({
      query: (arg) => ({
        url: '/purchase/invoices/lookups/all-contracts',
        params: arg && arg.q ? { q: arg.q } : {},
      }),
    }),
    invoiceWarehousesLookup: b.query<InvoiceLookupResponse, void>({
      query: () => '/purchase/invoices/lookups/warehouses',
    }),
    invoicePriceUnitsLookup: b.query<InvoiceLookupResponse, void>({
      query: () => '/purchase/invoices/lookups/price-units',
    }),
  }),
})

export const {
  useListInvoicesQuery,
  useGetInvoiceQuery,
  useLazyGetInvoiceQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
  useBulkDeleteInvoiceMutation,
  useSetInvoiceDeclarationDateMutation,
  useListInvoiceSuggestHoadonQuery,
  useSaveInvoiceSuggestHoadonMutation,
  useLazyInvoiceContractSnapshotQuery,
  useInvoicePContractsLookupQuery,
  useLazyInvoicePContractsLookupQuery,
  useInvoiceConsignmentContractsLookupQuery,
  useLazyInvoiceConsignmentContractsLookupQuery,
  useInvoiceAllContractsLookupQuery,
  useLazyInvoiceAllContractsLookupQuery,
  useInvoiceWarehousesLookupQuery,
  useInvoicePriceUnitsLookupQuery,
} = procurementInvoiceApi

// ===================== procurementPayDataApi =====================
/**
 * payDataApi — RTK Query slice cho /purchase/pay-data/* (Procurement Pay Data).
 * Tag types: 'PayData' (list) + 'PayDataDetail' (single).
 */

export const payDataApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listPayData: b.query<PageResponse<PayDataListItem>, PayDataListQuery | void>({
      query: (params) => ({ url: '/purchase/pay-data/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'PayData' as const, id: it.paydata_p_id })),
              { type: 'PayData' as const, id: 'LIST' },
            ]
          : [{ type: 'PayData' as const, id: 'LIST' }],
    }),
    getPayData: b.query<DataResponse<PayDataDetail>, number>({
      query: (id) => `/purchase/pay-data/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PayDataDetail', id }],
    }),
    createPayData: b.mutation<DataResponse<PayDataDetail>, PayDataWrite>({
      query: (body) => ({ url: '/purchase/pay-data/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'PayData', id: 'LIST' }],
    }),
    updatePayData: b.mutation<DataResponse<PayDataDetail>, { id: number; body: PayDataWrite }>({
      query: ({ id, body }) => ({ url: `/purchase/pay-data/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'PayData', id: arg.id },
        { type: 'PayData', id: 'LIST' },
        { type: 'PayDataDetail', id: arg.id },
      ],
    }),
    deletePayData: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/purchase/pay-data/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'PayData', id },
        { type: 'PayData', id: 'LIST' },
      ],
    }),
    /** Multi-delete (ticked rows). */
    deleteManyPayData: b.mutation<MessageResponse, number[]>({
      query: (ids) => ({ url: '/purchase/pay-data/delete-many', method: 'POST', body: ids }),
      invalidatesTags: [{ type: 'PayData', id: 'LIST' }],
    }),
    /** access-suggest: next xntt_no theo contract. */
    suggestXnttNo: b.query<{ suggestedNo: string }, { contractNo: string }>({
      query: ({ contractNo }) =>
        `/purchase/pay-data/suggest/xntt?contractNo=${encodeURIComponent(contractNo)}`,
      transformResponse: (r: { suggested_no?: string }) => ({ suggestedNo: r.suggested_no ?? '' }),
      keepUnusedDataFor: 0,
    }),
    checkXnttNoExists: b.query<{ exists: boolean }, { xnttNo: string }>({
      query: ({ xnttNo }) => `/purchase/pay-data/check/xntt?xnttNo=${encodeURIComponent(xnttNo)}`,
      keepUnusedDataFor: 0,
    }),
    // Lookups
    payDataPaymentTypeLookup: b.query<PayDataLookupResponse, void>({
      query: () => '/purchase/pay-data/lookups/payment-type',
    }),
    payDataBankLookup: b.query<PayDataLookupResponse, { cpCode?: string } | void>({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/bank',
        params: arg && arg.cpCode ? { cp_code: arg.cpCode } : {},
      }),
    }),
    payDataBankAccountLookup: b.query<PayDataLookupResponse, { cpCode?: string; bank?: string } | void>({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/bank-account',
        params: {
          ...(arg && arg.cpCode ? { cp_code: arg.cpCode } : {}),
          ...(arg && arg.bank ? { bank: arg.bank } : {}),
        },
      }),
    }),
    payDataTruckIdLookup: b.query<PayDataLookupResponse, { contractNo?: string } | void>({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/truck-id',
        params: arg && arg.contractNo ? { contract_no: arg.contractNo } : {},
      }),
    }),
    payDataInvoiceNoLookup: b.query<
      PayDataLookupResponse,
      { contractNo?: string; contractNo2?: string } | void
    >({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/invoice-no',
        params: {
          ...(arg && arg.contractNo ? { contract_no: arg.contractNo } : {}),
          ...(arg && arg.contractNo2 ? { contract_no2: arg.contractNo2 } : {}),
        },
      }),
    }),
    payDataContractsLookup: b.query<PayDataLookupResponse, { kind?: 'p' | 'npe'; q?: string } | void>({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/contracts',
        params: {
          ...(arg && arg.kind ? { kind: arg.kind } : {}),
          ...(arg && arg.q ? { q: arg.q } : {}),
        },
      }),
    }),
    payDataContractInfo: b.query<Record<string, unknown>, { contractNo: string }>({
      query: ({ contractNo }) =>
        `/purchase/pay-data/contract-info?contractNo=${encodeURIComponent(contractNo)}`,
      keepUnusedDataFor: 0,
    }),
    /** Invoice snapshot (invoice_date / amount / vat) theo invoice_no — mirror
     *  DLookup Access của form Add (Invoice Information RO fields). */
    payDataInvoiceInfo: b.query<Record<string, unknown>, { invoiceNo: string }>({
      query: ({ invoiceNo }) =>
        `/purchase/pay-data/lookups/invoice-info?invoiceNo=${encodeURIComponent(invoiceNo)}`,
      keepUnusedDataFor: 0,
    }),
    /** Price units + x_to_mt — Check_Amount quy đổi USD/VND (Access public_price_unit). */
    payDataPriceUnitsLookup: b.query<{ price_unit: string; x_to_mt: number | null }[], void>({
      query: () => '/purchase/pay-data/lookups/price-units',
    }),
    /** Provisional snapshot theo contract+truck (Access Check_Amount →
     *  v_paydata_p_checkprovisional auto-fill futures/fx/price/price_unit). */
    payDataProvisionalCheck: b.query<
      Record<string, unknown>,
      { contractNo: string; truckId: string }
    >({
      query: ({ contractNo, truckId }) =>
        `/purchase/pay-data/lookups/provisional-check?contractNo=${encodeURIComponent(contractNo)}&truckId=${encodeURIComponent(truckId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Pay-Confirm print preview (BE chọn variant theo row). */
    printPayConfirm: b.query<DataResponse<PayDataPrintResult>, number>({
      query: (id) => `/purchase/pay-data/${id}/print`,
    }),
    /** Send-XNTT: prefill mở Outlook (mailto) — to=email supplier, subject, body.
     *  KHÔNG đính kèm (giới hạn mailto); Excel để dành bản Graph upgrade. */
    xnttMailDraft: b.query<PayDataMailDraft, number[]>({
      query: (ids) => `/purchase/pay-data/mail/xntt?paydataIds=${ids.join(',')}`,
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useListPayDataQuery,
  useGetPayDataQuery,
  useLazyGetPayDataQuery,
  useCreatePayDataMutation,
  useUpdatePayDataMutation,
  useDeletePayDataMutation,
  useDeleteManyPayDataMutation,
  useLazySuggestXnttNoQuery,
  useLazyCheckXnttNoExistsQuery,
  usePayDataPaymentTypeLookupQuery,
  usePayDataBankLookupQuery,
  useLazyPayDataBankLookupQuery,
  useLazyPayDataBankAccountLookupQuery,
  usePayDataTruckIdLookupQuery,
  useLazyPayDataTruckIdLookupQuery,
  useLazyPayDataInvoiceNoLookupQuery,
  useLazyPayDataContractsLookupQuery,
  usePayDataContractsLookupQuery,
  useLazyPayDataContractInfoQuery,
  useLazyPayDataInvoiceInfoQuery,
  usePayDataPriceUnitsLookupQuery,
  useLazyPayDataProvisionalCheckQuery,
  useLazyPrintPayConfirmQuery,
  useLazyXnttMailDraftQuery,
} = payDataApi

// ===================== procurementPayDataInvoiceApi =====================
/**
 * payDataInvoiceApi — RTK Query slice cho /purchase/pay-data-invoice/*.
 *
 * File RIÊNG (không sửa payDataApi.ts dùng chung). injectEndpoints trên baseApi,
 * reuse tag 'PayData' để invalidate list sau khi gán invoice.
 *
 * Backend: app/api/v1/endpoints/purchase/pay_data_invoice.py
 *   GET  /purchase/pay-data-invoice/list?contract_no=&paydata_ids=
 *   POST /purchase/pay-data-invoice/attach
 */

/** Một dòng invoice của contract (đọc public.v_invoice_p). */
export interface PayDataInvoiceItem {
  invoice_id: number
  invoice_no: string | null
  contract_no: string | null
  invoice_date: string | null
  warehouse: string | null
  quantity: number | null
  price_vnd_kg: number | null
  quality_code: string | null
  certification: string | null
  license_cert_no: string | null
  total_payment: number | null
  total_amount: number | null
  vat_amount: number | null
  total_amount_after_tax: number | null
  note: string | null
  date_update: string | null
  person_update: string | null
}

/** Tóm tắt paydata cha (DSum theo ticked rows). */
export interface PayDataInvoiceSummary {
  contract_no: string | null
  supplier: string | null
  price_unit: string | null
  contract_type: string | null
  quantity: number
  requested_usd: number
  requested_vnd: number
  advanced: number
}

export interface PayDataInvoiceListResponse {
  summary: PayDataInvoiceSummary
  items: PayDataInvoiceItem[]
  total: number
}

/** Một dòng paydata đã tick + giá trị edit (mirror Access subFormEdit). */
export interface AttachInvoiceRow {
  paydata_p_id: number
  invoice_no: string | null
  paid_usd: number | null
  fx: number | null
  vat_amount_vnd: number | null
  paid_vnd: number | null
}

export interface AttachInvoiceRequest {
  contract_no: string
  rows: AttachInvoiceRow[]
}

export interface AttachInvoiceResult {
  updated: number
}

interface AttachInvoiceResponse {
  data: AttachInvoiceResult
  message: string
}

export const payDataInvoiceApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listPayDataInvoices: b.query<
      PayDataInvoiceListResponse,
      { contractNo: string; paydataIds: number[] }
    >({
      query: ({ contractNo, paydataIds }) => {
        // FastAPI `list[int]` cần REPEATED params (paydata_ids=1&paydata_ids=2),
        // KHÔNG phải CSV — fetchBaseQuery mặc định flatten array thành CSV → 422.
        const sp = new URLSearchParams()
        sp.set('contract_no', contractNo)
        for (const id of paydataIds) sp.append('paydata_ids', String(id))
        return { url: `/purchase/pay-data-invoice/list?${sp.toString()}` }
      },
      providesTags: [{ type: 'PayData' as const, id: 'INVOICE_LIST' }],
    }),
    attachPayDataInvoice: b.mutation<AttachInvoiceResponse, AttachInvoiceRequest>({
      query: (body) => ({
        url: '/purchase/pay-data-invoice/attach',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'PayData' as const, id: 'LIST' }],
    }),
  }),
  overrideExisting: false,
})

export const { useListPayDataInvoicesQuery, useAttachPayDataInvoiceMutation } =
  payDataInvoiceApi

// ===================== procurementPayDataVatApi =====================
/**
 * payDataVatApi — RTK Query slice cho /purchase/pay-data-vat/* (Procurement
 * Pay Data → Send VAT). Dedicated slice (KHÔNG đụng payDataApi.ts).
 *
 * BE trả snake_case (không alias) → giữ nguyên shape snake_case ở FE để bảng
 * map 1:1 subform `frmProcurementQryPayDataSendVAT`.
 */

export interface VatInvoiceItem {
  invoice_id: number | null
  invoice_no: string | null
  contract_no: string | null
  invoice_date: string | null
  cp_code: string | null
  contract_name: string | null
  vat_amount: number | null
  total_amount: number | null
  total_amount_after_tax: number | null
  paid_vat_date: string | null
  paid_vat_value: number | null
  paid_vat_txt: string | null
}

export interface VatInvoiceListResponse {
  items: VatInvoiceItem[]
  total: number
}

export interface VatBankItem {
  bank_account: string | null
  cp_code: string | null
  bank: string | null
  bank_info: string | null
}

export interface VatBankListResponse {
  items: VatBankItem[]
  total: number
}

export interface VatInvoiceListQuery {
  cp_code?: string
  contract_no?: string
  date_from?: string
  date_to?: string
}

export interface VatSubmitInvoice {
  invoice_no: string
  contract_no: string
  vat_amount: number | null
}

export interface VatSubmitPayload {
  paid_date: string | null
  bank_account: string | null
  cp_code?: string | null
  invoices: VatSubmitInvoice[]
}

export interface VatSubmitResult {
  inserted: number
  soft_deleted: number
  message: string
}

export const payDataVatApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listVatInvoices: b.query<VatInvoiceListResponse, VatInvoiceListQuery | void>({
      query: (params) => ({ url: '/purchase/pay-data-vat/invoices', params: params ?? {} }),
    }),
    listVatBanks: b.query<VatBankListResponse, { cp_code?: string } | void>({
      query: (params) => ({ url: '/purchase/pay-data-vat/banks', params: params ?? {} }),
    }),
    submitVat: b.mutation<VatSubmitResult, VatSubmitPayload>({
      query: (body) => ({ url: '/purchase/pay-data-vat/submit', method: 'POST', body }),
      // Invalidate Pay Data list (declared VAT can change downstream views).
      invalidatesTags: [{ type: 'PayData', id: 'LIST' }],
    }),
  }),
  overrideExisting: false,
})

export const { useListVatInvoicesQuery, useListVatBanksQuery, useSubmitVatMutation } =
  payDataVatApi

// ===================== procurementReferenceApi =====================
/**
 * procurementReferenceApi — RTK Query slice cho /purchase/references/* (Procurement
 * References switchboard, Access `frmProcurementMainSubFormReferences`). Read-only:
 * mỗi pageKey trả 1 datasheet. Mirrors sale/purchase reference endpoints.
 */

export type ProcurementReferenceRow = Record<string, unknown>

export interface ProcurementReferenceRowsQuery {
  pageKey: string
  limit?: number
  /** Shared "Inbound Logistics" filter — only ContractStatistics/PendingPayment use these. */
  startDate?: string | null
  endDate?: string | null
  isForcedClose?: boolean
}

export interface ProcurementReferencePageResponse {
  pageKey: string
  sourceForm: string
  sourceQuery: string
  items: ProcurementReferenceRow[]
  total: number
}

export const procurementReferenceApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    getProcurementReferenceRows: b.query<
      ProcurementReferencePageResponse,
      ProcurementReferenceRowsQuery
    >({
      query: ({ pageKey, limit = 1_000_000, startDate, endDate, isForcedClose }) => ({
        url: `/references/${encodeURIComponent(pageKey)}`,
        params: {
          limit,
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          ...(isForcedClose !== undefined ? { is_forced_close: isForcedClose } : {}),
        },
      }),
      // Reload chung (ProcurementPage) refetch được từng reference page qua tag.
      providesTags: (res) =>
        res
          ? [
              { type: 'ProcReference' as const, id: res.pageKey },
              { type: 'ProcReference' as const, id: 'LIST' },
            ]
          : [{ type: 'ProcReference' as const, id: 'LIST' }],
    }),
  }),
})

export const { useGetProcurementReferenceRowsQuery } = procurementReferenceApi
